import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';
import { StepsSwitchSchema } from './steps-switch';
import { RECOVERY_PLANS_QUERY_KEY } from './use-load-recovery-plans';

const URL = '/default_recovery_plan';

const UpdateDefaultRecoveryPlanVariablesSchema = z.object({
  recoveryPlanId: z.string(),
  stepsSwitch: StepsSwitchSchema.default([]),
});

type UpdateDefaultRecoveryPlanVariables = z.input<typeof UpdateDefaultRecoveryPlanVariablesSchema>;

export async function updateDefaultRecoveryPlanFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateDefaultRecoveryPlanVariables,
) {
  const instance = await axiosInstance;

  return instance.put(URL, UpdateDefaultRecoveryPlanVariablesSchema.parse(variables));
}

export function useUpdateDefaultRecoveryPlan() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateDefaultRecoveryPlanVariables>({
    mutationFn: variables => updateDefaultRecoveryPlanFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: RECOVERY_PLANS_QUERY_KEY });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
      // The default plan is still read from the legacy configuration by the
      // client, invoice and batch-action screens.
      client.invalidateQueries({ queryKey: ['company-configuration'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateDefaultRecoveryPlanVariables,
    'updateDefaultRecoveryPlan'
  >('updateDefaultRecoveryPlan', mutation);
}
